import { getCookie, setCookie } from './_utilities.js';

/** Class containing orchestration logic and event handlers for the exit intent popup. */
class ExitIntentPopup {
    /* exit intent popup element */
    exitIntentPopupElement;
    /* exit-intent-popup-acknowledgement cookie value */
    cookieValue;
    /* exit intent popup close button element */
    closeButton;
    /* setCookie callback from utilities */
    setCookie;
    /* exit intent popup time delay attribute */
    timeDelay;
    /* abortController used to remove mouse event handler */
    mouseEventAbortController;

    /**
     * Retrieves DOM elements for the exit intent popup, retrieves exit intent cookie value, and initializes
     * functionality for the exit intent popup if the popup is loaded on the page and the exit intent cookie is false
     * or not set. If a time delay is set, opens the popup after the set time delay. If a time delay is not set, adds a
     * one-time event listener to open the popup if the pointer device leaves the active window.
     *
     * @param {Function} getCookieValue - Callback from utilities module that retrieves a cookie value.
     * @param {Function} setCookieValue - Callback from utilities module that sets a new cookie.
     */
    constructor(getCookieValue, setCookieValue) {
        this.exitIntentPopupElement =
            document.querySelector('.exit_intent_popup');
        this.cookieValue = getCookieValue('exit-intent-popup-acknowledgement');

        if (this.exitIntentPopupElement && !this.cookieValue) {
            this.setCookie = setCookieValue;
            this.addCloseButtonEventListener();
            this.timeDelay =
                this.exitIntentPopupElement.getAttribute('data-time-delay');
            if (this.timeDelay) {
                this.openPopupAfterTimeDelay();
            } else {
                this.addMouseoutEventListener();
            }
        }
    }

    /** Adds one-time event listener to close button that sets acknowledgement cookie. */
    addCloseButtonEventListener() {
        this.closeButton = this.exitIntentPopupElement.querySelector(
            '.close_exit_intent_popup_button'
        );
        this.closeButton.addEventListener(
            'click',
            () => this.setPopupCookie(this.exitIntentPopupElement),
            { once: true }
        );
    }

    /**
     * Sets exit-intent-popup-acknowledgement cookie with a value of true and an expiration date of current session.
     * Closes popup upon user clicking the close button.
     *
     * @param {Element} exitIntentPopupElement - Exit intent popup element.
     */
    setPopupCookie(exitIntentPopupElement) {
        this.setCookie('exit-intent-popup-acknowledgement', 'true', 1);
        exitIntentPopupElement.classList.remove('show-exit-intent-popup');
    }

    /** Opens the exit intent popup after the set time delay. */
    openPopupAfterTimeDelay() {
        setTimeout(this.openPopup.bind(this), this.timeDelay);
    }

    /** Displays the exit intent popup. */
    openPopup() {
        document
            .querySelector('.exit_intent_popup')
            .classList.add('show-exit-intent-popup');
    }

    /**
     * Adds event handler and abort controller to document mouseout event to display popup on the first mouseout
     * event that leaves the active window.
     */
    addMouseoutEventListener() {
        this.mouseEventAbortController = new AbortController();
        document.addEventListener(
            'mouseout',
            this.mouseoutEventHandler.bind(this),
            { signal: this.mouseEventAbortController.signal }
        );
    }

    /**
     * Event handler for mouseout event. Fires when pointer device leaves the active window. If the pointer device
     * leaves the active window, removes the event listener and opens the exit intent popup. This event listener is
     * added if a time delay is not set for the exit intent popup.
     *
     * @param {MouseEvent} e - Mouseout event for the document
     */
    mouseoutEventHandler(e) {
        if (!e.relatedTarget) {
            this.mouseEventAbortController.abort();
            this.openPopup();
        }
    }
}

const init = () => {
    new ExitIntentPopup(getCookie, setCookie);
};

export { init };
